import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import './ProductDetails.css';
import { FaCheckCircle, FaChevronDown, FaChevronUp} from 'react-icons/fa';
import productsData from '../data/products.json';
import { optimizeImage } from '../utils/imageOptimizer';

const ProductDetails = () => {
    const { id } = useParams();
    const [openAccordion, setOpenAccordion] = useState(null);
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    const product = productsData.find(p => p.id === id);
    
    if (!product) {
        return (
            <div className="product-not-found container">
                <h2>Product Not Found</h2>
                <Link to="/" className="btn btn-outline">Back to Home</Link>
            </div>
        );
    }

    const toggleAccordion = (id) => {
        setOpenAccordion(openAccordion === id ? null : id);
    };

    // Extracting and formatting net weight to display sizes cleanly (e.g. "450g / 1kg / 2.8kg / 6.5kg")
    let displayNetWeight = product.netWeight || '';

    // Clean up packaging info if trapped in netWeight string (e.g., "85g pack | Units per...")
    if (displayNetWeight.includes('|')) {
        const parts = displayNetWeight.split('|').map(p => p.trim());
        const weightParts = parts.filter(part => {
            const lowerPart = part.toLowerCase();
            return !lowerPart.includes('units per box') && !lowerPart.includes('units per case');
        });
        displayNetWeight = weightParts.join(' / ');
    }

    // Remove prefix "Available packs:" or "Available pack:" if present
    displayNetWeight = displayNetWeight.replace(/Available packs?:/i, '').trim();

    // Clean up price details (e.g., " - ₹199 onwards" or " - ₹150") to display only weights
    displayNetWeight = displayNetWeight.replace(/\s*-\s*₹[^/|]+/g, '');

    // Normalize separators: replace all '|' and normalize spaces around '/'
    displayNetWeight = displayNetWeight.split(/[|/]/).map(p => p.trim()).join(' / ');

    // Related products logic
    const categoryProducts = productsData.filter(p => p.type === product.type && p.category === product.category);
    const currentIndex = categoryProducts.findIndex(p => p.id === product.id);
    let relatedIndices = [];
    const count = categoryProducts.length;
    
    if (count <= 5) {
        relatedIndices = Array.from({ length: count }, (_, i) => i).filter(i => i !== currentIndex);
    } else {
        relatedIndices = [
            (currentIndex - 2 + count) % count,
            (currentIndex - 1 + count) % count,
            (currentIndex + 1) % count,
            (currentIndex + 2) % count
        ];
    }
    const otherProducts = relatedIndices.map(idx => categoryProducts[idx]);



    return (
        <div className="product-details-page">
            <div className="container main-content-padding">
                {/* BREADCRUMB */}
                <nav className="product-breadcrumb">
                    <Link to="/">Home</Link>
                    <span>&gt;</span>
                    <Link to={`/${product.type}-products`}>{product.type === 'dog' ? 'Dog Products' : product.type === 'cat' ? 'Cat Products' : 'Pet Products'}</Link>
                    <span>&gt;</span>
                    <span className="current">{product.name}</span>
                </nav>

                <div className="product-layout-main">
                    {/* LEFT: IMAGE */}
                    <div className="product-visual-col">
                        <div className="product-image-container">
                            <img key={product.id} src={optimizeImage(product.img, { width: 600, height: 600 })} alt={product.name} width="600" height="600" />
                            {product.isComingSoon && <div className="coming-soon-label">Coming Soon</div>}
                        </div>
                    </div>

                    {/* RIGHT: INFO & ACCORDION */}
                    <div className="product-info-col">
                        <h1 className="product-name">{product.name}</h1>
                        {!product.isComingSoon && <p className="product-price-val">{product.price}</p>}
                        
                        <p className="product-long-desc">{product.desc}</p>
                        
                        <div className="product-badges">
                            <span className="badge-item">
                                <FaCheckCircle className="badge-icon" /> In Stock!
                            </span>
                            <span className="badge-item">
                                <FaCheckCircle className="badge-icon" /> Quality Assured
                            </span>
                        </div>

                        <div className="product-net-weight">
                            <h3 className="packaging-label-title">Net Weight</h3>
                            <p>{displayNetWeight}</p>
                        </div>

                        <Link to="/contact#contact-form" className="enquire-now-btn">
                            ENQUIRE NOW
                        </Link>

                        {/* ACCORDION LIST */}
                        <div className="product-vertical-accordion">
                            <div className={`accordion-row ${openAccordion === 'ingredients' ? 'active' : ''}`} onClick={() => toggleAccordion('ingredients')}>
                                <div className="accordion-row-header">
                                    <span>Ingredients</span>
                                    {openAccordion === 'ingredients' ? <FaChevronUp /> : <FaChevronDown />}
                                </div>
                                <div className="accordion-row-content">
                                    <p>{product.ingredients}</p>
                                </div>
                            </div>

                            {/* Nutrition Info - Only show if it exists and is not empty */}
                            {product.nutritionInfo && typeof product.nutritionInfo === 'object' && Object.keys(product.nutritionInfo).length > 0 && (
                                <div className={`accordion-row ${openAccordion === 'nutrition' ? 'active' : ''}`} onClick={() => toggleAccordion('nutrition')}>
                                    <div className="accordion-row-header">
                                        <span>Nutrition Information</span>
                                        {openAccordion === 'nutrition' ? <FaChevronUp /> : <FaChevronDown />}
                                    </div>
                                    <div className="accordion-row-content">
                                        <ul className="nutrition-ul">
                                            {Object.entries(product.nutritionInfo).map(([key, value]) => (
                                                <li key={key}><strong>{key}:</strong> {value}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {/* Usage/Feeding Guide - Rename based on available data */}
                            {(product.usageGuide || product.feedingGuide) && (
                                <div className={`accordion-row ${openAccordion === 'guide' ? 'active' : ''}`} onClick={() => toggleAccordion('guide')}>
                                    <div className="accordion-row-header">
                                        <span>{product.usageGuide ? 'Usage Guide' : 'Feeding Guide'}</span>
                                        {openAccordion === 'guide' ? <FaChevronUp /> : <FaChevronDown />}
                                    </div>
                                    <div className="accordion-row-content">
                                        {(() => {
                                            const text = product.usageGuide || product.feedingGuide;
                                            if (!text) return null;
                                            if (text.includes('|')) {
                                                const parts = text.split('|').map(p => p.trim());
                                                let subheading = null;
                                                let firstLineText = parts[0];
                                                
                                                if (firstLineText.toLowerCase().startsWith('daily treat quantity:')) {
                                                    subheading = 'Daily Treat Quantity:';
                                                    firstLineText = firstLineText.substring('daily treat quantity:'.length).trim();
                                                }
                                                
                                                return (
                                                    <div className="feeding-guide-container">
                                                        {subheading && <div className="guide-subheading">{subheading}</div>}
                                                        
                                                        {parts.length === 1 ? (
                                                            (() => {
                                                                const periodIdx = firstLineText.indexOf('.');
                                                                if (periodIdx !== -1) {
                                                                    const treatQty = firstLineText.substring(0, periodIdx).trim();
                                                                    const note = firstLineText.substring(periodIdx + 1).trim();
                                                                    return (
                                                                        <React.Fragment>
                                                                            <div className="guide-line">{treatQty}</div>
                                                                            {note && <div className="guide-note">{note}</div>}
                                                                        </React.Fragment>
                                                                    );
                                                                }
                                                                return <div className="guide-line">{firstLineText}</div>;
                                                            })()
                                                        ) : (
                                                            <React.Fragment>
                                                                <div className="guide-line">{firstLineText}</div>
                                                                {parts.slice(1, parts.length - 1).map((part, idx) => (
                                                                    <div className="guide-line" key={idx}>{part}</div>
                                                                ))}
                                                                {(() => {
                                                                    const lastPart = parts[parts.length - 1];
                                                                    const periodIdx = lastPart.indexOf('.');
                                                                    if (periodIdx !== -1) {
                                                                        const treatQty = lastPart.substring(0, periodIdx).trim();
                                                                        const note = lastPart.substring(periodIdx + 1).trim();
                                                                        return (
                                                                            <React.Fragment>
                                                                                <div className="guide-line">{treatQty}</div>
                                                                                {note && <div className="guide-note">{note}</div>}
                                                                            </React.Fragment>
                                                                        );
                                                                    }
                                                                    return <div className="guide-line">{lastPart}</div>;
                                                                })()}
                                                            </React.Fragment>
                                                        )}
                                                    </div>
                                                );
                                            }
                                            return text.split('\n').map((line, i) => <p key={i}>{line}</p>);
                                        })()}
                                    </div>
                                </div>
                            )}

                           {/*  <div className={`accordion-row ${openAccordion === 'storage' ? 'active' : ''}`} onClick={() => toggleAccordion('storage')}>
                                <div className="accordion-row-header">
                                    <span>Storage Instructions</span>
                                    {openAccordion === 'storage' ? <FaChevronUp /> : <FaChevronDown />}
                                </div>
                                <div className="accordion-row-content">
                                    <p>{product.storageInstructions}</p>
                                </div>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* YOU MAY ALSO LIKE SECTION */}
            <section className="you-may-like-section">
                <div className="container">
                    <h3 className="section-title-center">You May Also Like</h3>
                    <div className="you-may-like-grid">
                        {otherProducts.map((p) => (
                            <Link to={`/${product.type}-products/${p.id}`} key={p.id} className="suggestion-card">
                                <div className="suggestion-img-wrapper">
                                    <img src={optimizeImage(p.img, { width: 300, height: 300 })} alt={p.name} loading="lazy" width="300" height="300" />
                                    {p.isComingSoon && <div className="coming-soon-label">Coming Soon</div>}
                                    <div className="suggestion-hover-btn">
                                        <span>View Details</span>
                                    </div>
                                </div>
                                <div className="suggestion-info">
                                    <h4 className="suggestion-card-name">{p.name}</h4>
                                    {!p.isComingSoon && <p>{p.price}</p>}
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductDetails;
